import axios from '../../../utils/axiosConfig';

// Format page counts as film-industry 1/8ths
export const formatPagesInEighths = (pages) => {
  if (!pages && pages !== 0) return '';
  let value = pages;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    if (!isNaN(parsed)) value = parsed;
  }
  // Convert to eighths
  const eighths = Math.round(value * 8);
  const whole = Math.floor(eighths / 8);
  const remainder = eighths % 8;
  if (remainder === 0) return `${whole}`;
  return `${whole} ${remainder}/8`;
};

// Format a single raw scene to the standardized schedule row used by the UI/print
export const formatSceneForSchedule = (scene) => {
  if (!scene) {
    return {
      setScene: 'Unknown',
      sceneNumber: 'N/A',
      description: 'Unknown scene',
      cast: '',
      dayNight: 'DAY',
      pages: '',
      location: 'Unknown'
    };
  }

  // Scene number
  let sceneNumber = '';
  if (scene.sceneNumber) {
    if (typeof scene.sceneNumber === 'object' && scene.sceneNumber.scriptOrder) {
      sceneNumber = scene.sceneNumber.scriptOrder;
    } else if (typeof scene.sceneNumber === 'object' && scene.sceneNumber.standard) {
      sceneNumber = scene.sceneNumber.standard.toString();
    } else {
      sceneNumber = scene.sceneNumber.toString();
    }
  } else if (scene.scene_number) {
    sceneNumber = scene.scene_number;
  } else if (scene.number) {
    sceneNumber = scene.number;
  } else if (scene.id && typeof scene.id === 'string' && scene.id.includes('-')) {
    const match = scene.id.match(/scene-([\w\d]+)/i);
    if (match && match[1]) sceneNumber = match[1];
  }

  // INT/EXT detection from location_type
  let intExt = '';
  const locationType = (scene.location_type || '').toUpperCase();
  if (locationType.includes('INT') && locationType.includes('EXT')) intExt = 'I/E';
  else if (locationType.includes('INT')) intExt = 'INT';
  else if (locationType.includes('EXT')) intExt = 'EXT';

  // Description
  let description = '';
  if (scene.description) description = scene.description;
  else if (scene.synopsis) description = scene.synopsis;
  else if (scene.action) description = scene.action;
  else if (scene.summary) description = scene.summary;

  // Set/Location string
  let setScene = '';
  if (scene.setting) setScene = scene.setting;
  else if (scene.location) setScene = scene.location;
  else if (scene.slug_line) setScene = scene.slug_line;
  else if (scene.slugLine) setScene = scene.slugLine;
  else if (scene.slug) setScene = scene.slug;

  // Day/Night - use full time value, not just first character
  let dayNight = scene.time ? scene.time.toUpperCase().trim() : '';
  if (!dayNight && intExt) dayNight = intExt;
  if (!dayNight) {
    if (scene.dayNight) dayNight = scene.dayNight;
    else if (scene.day_night) dayNight = scene.day_night;
    else if (scene.time_of_day) dayNight = scene.time_of_day;
    else if (scene.timeOfDay) dayNight = scene.timeOfDay;
    else if (setScene) {
      const upper = setScene.toUpperCase();
      if (upper.includes('DAY')) dayNight = 'DAY';
      else if (upper.includes('NIGHT')) dayNight = 'NIGHT';
      else if (upper.includes('EVENING')) dayNight = 'EVENING';
      else if (upper.includes('MORNING')) dayNight = 'MORNING';
      else if (upper.includes('DAWN')) dayNight = 'DAWN';
      else if (upper.includes('DUSK')) dayNight = 'DUSK';
      else if (upper.includes('SUNSET')) dayNight = 'SUNSET';
      else if (upper.includes('SUNRISE')) dayNight = 'SUNRISE';
      else if (upper.includes('CONTINUOUS')) dayNight = 'CONTINUOUS';
      else if (upper.includes('CONT.')) dayNight = 'CONT.';
      else if (upper.includes('LATER')) dayNight = 'LATER';
      else if (upper.includes('SAME TIME')) dayNight = 'SAME TIME';
      else if (upper.includes('INT') && upper.includes('EXT')) dayNight = 'I/E';
      else if (upper.includes('I/E')) dayNight = 'I/E';
      else if (upper.includes('E/I')) dayNight = 'I/E';
      else if (upper.includes('INT')) dayNight = 'INT';
      else if (upper.includes('EXT')) dayNight = 'EXT';
    }
  }

  // Pages
  let pages = '';
  if (scene.pages) pages = formatPagesInEighths(scene.pages);
  else if (scene.page_count) pages = formatPagesInEighths(scene.page_count);
  else if (scene.page_length) pages = formatPagesInEighths(scene.page_length);
  else if (scene.length) pages = formatPagesInEighths(scene.length);

  return {
    setScene: setScene,
    sceneNumber: sceneNumber,
    description: description,
    cast: scene.characters ? scene.characters.join(', ') : '',
    dayNight: dayNight,
    pages: pages,
    location: setScene,
    location_type: intExt || locationType
  };
};

// Fetch all scenes and breaks once
const fetchScenesAndBreaks = async (projectId) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Authentication token not found');
  const headers = { 'Authorization': `Bearer ${token}`, 'x-auth-token': token };

  const [scenesResponse, breaksResponse] = await Promise.all([
    axios.get(`/api/v1/scripts/project/${projectId}/scenes`, { headers }),
    axios.get(`/api/v1/scripts/${projectId}/breaks`, { headers })
  ]);

  let scenes = [];
  if (Array.isArray(scenesResponse?.data?.scenes)) scenes = scenesResponse.data.scenes;
  else if (Array.isArray(scenesResponse?.data)) scenes = scenesResponse.data;
  else {
    const responseData = scenesResponse.data || {};
    const arrays = Object.values(responseData).filter(v => Array.isArray(v) && v.length > 0);
    if (arrays.length > 0) scenes = arrays.reduce((largest, cur) => cur.length > largest.length ? cur : largest, []);
  }

  let breaks = [];
  if (Array.isArray(breaksResponse?.data)) breaks = breaksResponse.data;
  else if (Array.isArray(breaksResponse?.data?.breaks)) breaks = breaksResponse.data.breaks;

  // Sort scenes
  const sortedScenes = [...scenes].sort((a, b) => {
    const orderA = a.order || a.scriptOrder || a.index || 0;
    const orderB = b.order || b.scriptOrder || b.index || 0;
    return orderA - orderB;
  });

  // Day breaks only, sorted
  const dayBreaks = breaks
    .filter(brk => brk.type === 'day' || brk.type === 'DAY_BREAK' || brk.type === 'DAY' || brk.breakType === 'day')
    .sort((a, b) => {
      const indexA = a.sceneIndex || a.afterSceneIndex || a.index || 0;
      const indexB = b.sceneIndex || b.afterSceneIndex || b.index || 0;
      return indexA - indexB;
    });

  return { sortedScenes, dayBreaks };
};

// Determine scenes for a specific shooting day based on day breaks
const getScenesForDay = (sortedScenes, dayBreaks, day) => {
  if (dayBreaks.length === 0) return day === 1 ? sortedScenes : [];
  if (day === 1) {
    const firstBreakIndex = dayBreaks.length > 0 ? (dayBreaks[0].sceneIndex || dayBreaks[0].afterSceneIndex || dayBreaks[0].index || 0) : sortedScenes.length;
    return sortedScenes.filter((_, idx) => idx <= firstBreakIndex);
  }
  const prevDayIndex = day - 1;
  const currDayIndex = day;
  const prevBreak = dayBreaks.find(b => (b.day === prevDayIndex || b.dayNumber === prevDayIndex || b.customDayNumber === prevDayIndex)) || (prevDayIndex < dayBreaks.length ? dayBreaks[prevDayIndex - 1] : null);
  const currBreak = dayBreaks.find(b => (b.day === currDayIndex || b.dayNumber === currDayIndex || b.customDayNumber === currDayIndex)) || (currDayIndex < dayBreaks.length ? dayBreaks[currDayIndex - 1] : null);
  if (!prevBreak && !currBreak) return [];
  const startIdx = prevBreak ? (prevBreak.sceneIndex || prevBreak.afterSceneIndex || prevBreak.index || 0) : -1;
  const endIdx = currBreak ? (currBreak.sceneIndex || currBreak.afterSceneIndex || currBreak.index || 0) : sortedScenes.length - 1;
  return sortedScenes.filter((_, idx) => idx > startIdx && idx <= endIdx);
};

// Get the scene number as a comparable value for script order
const getSceneNumberValue = (scene) => {
  if (!scene) return Infinity;
  
  let sceneNum = '';
  if (scene.sceneNumber) {
    if (typeof scene.sceneNumber === 'object') {
      sceneNum = scene.sceneNumber.scriptOrder || scene.sceneNumber.standard || '';
    } else {
      sceneNum = scene.sceneNumber.toString();
    }
  } else if (scene.scene_number) {
    sceneNum = scene.scene_number;
  } else if (scene.number) {
    sceneNum = scene.number;
  }
  
  // Parse scene number - handle formats like "1", "1A", "1B", etc.
  const match = sceneNum.toString().match(/^(\d+)([A-Za-z]*)$/);
  if (match) {
    const num = parseInt(match[1], 10);
    const suffix = match[2] ? match[2].charCodeAt(0) - 64 : 0; // A=1, B=2, etc.
    return num * 100 + suffix; // e.g., "1" = 100, "1A" = 101, "2" = 200
  }
  
  return parseInt(sceneNum, 10) || Infinity;
};

// Resolve CONT./CONTINUOUS/LATER scenes to their inherited time of day
// Uses ORIGINAL SCRIPT ORDER (scene numbers), not current stripboard order
// This ensures Scene 2 (CONT.) always inherits from Scene 1 regardless of reordering
const resolveInheritedTimeByScriptOrder = (allScenes, targetScene) => {
  const inheritanceMarkers = ['CONTINUOUS', 'CONT.', 'CONT', 'LATER', 'SAME', 'SAME TIME', 'IMMEDIATELY FOLLOWING', 'IMMEDIATELY', 'FOLLOWING'];
  
  // Sort all scenes by their original script order (scene number)
  const scenesByScriptOrder = [...allScenes].sort((a, b) => {
    return getSceneNumberValue(a) - getSceneNumberValue(b);
  });
  
  // Find the target scene's position in script order
  const targetSceneNum = getSceneNumberValue(targetScene);
  const targetIndex = scenesByScriptOrder.findIndex(s => s.id === targetScene.id);
  
  if (targetIndex <= 0) return null;
  
  // Look backwards through scenes in SCRIPT ORDER to find the inherited time
  for (let i = targetIndex - 1; i >= 0; i--) {
    const prevScene = scenesByScriptOrder[i];
    const prevTime = (prevScene.time || prevScene.timeOfDay || '').toUpperCase().trim();
    
    // Check if previous scene also has an inheritance marker
    const isInheritance = inheritanceMarkers.some(marker => 
      prevTime === marker || prevTime.includes(marker)
    );
    
    if (!isInheritance && prevTime) {
      return prevTime;
    }
  }
  
  return null; // No definitive time found
};

// Format scenes with resolved time of day for CONT./CONTINUOUS scenes
const formatScenesWithResolvedTime = (scenes, allScenes) => {
  return scenes.map(scene => {
    const formatted = formatSceneForSchedule(scene);
    const time = (scene.time || scene.timeOfDay || '').toUpperCase().trim();
    const inheritanceMarkers = ['CONTINUOUS', 'CONT.', 'CONT', 'LATER', 'SAME', 'SAME TIME', 'IMMEDIATELY FOLLOWING', 'IMMEDIATELY', 'FOLLOWING'];
    
    // Check if this scene has an inheritance marker
    const isInheritance = inheritanceMarkers.some(marker => 
      time === marker || time.includes(marker)
    );
    
    if (isInheritance) {
      // Resolve based on ORIGINAL SCRIPT ORDER, not current stripboard order
      const resolvedTime = resolveInheritedTimeByScriptOrder(allScenes, scene);
      if (resolvedTime) {
        // Update the dayNight field with the resolved time
        formatted.dayNight = resolvedTime;
        // Keep original time in a separate field for reference
        formatted.originalTime = time;
      }
    }
    
    return formatted;
  });
};

// Public API: fetch and format schedules for a day and the next day
export const getSchedulesForDay = async (projectId, day) => {
  const { sortedScenes, dayBreaks } = await fetchScenesAndBreaks(projectId);
  const todayRaw = getScenesForDay(sortedScenes, dayBreaks, day);
  const nextRaw = getScenesForDay(sortedScenes, dayBreaks, day + 1);
  return {
    // Use the full sortedScenes array to resolve inherited times correctly
    schedule: formatScenesWithResolvedTime(todayRaw, sortedScenes),
    nextDaySchedule: formatScenesWithResolvedTime(nextRaw, sortedScenes)
  };
};
